// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId, useState } from 'react';

import type { SurveyType } from '../../../api/types';
import { candidates, layerExists, layerText, otherLayerLine, type Buildup } from '../../../bygning/buildups';
import { SelectButton } from '../../Select';
import { TextLink } from '../../TextLink';
import styles from './Inspector.module.css';

export interface BuildupPickerProps {
  type: SurveyType;
  /** Every type: a layer already split off says so instead of offering it again. */
  types: readonly SurveyType[];
  /** What to pick from: the server's library (`GET /buildups`) or the embedded copy. */
  library: readonly Buildup[];
  current: Buildup | null;
  /** The parts disagree on the stored build-up. */
  mixed?: boolean;
  /** Offer "Ingen opbygning" (a server that keeps `buildup_id` can clear it). */
  clearable?: boolean;
  canEdit: boolean;
  busy: boolean;
  onPick: (b: Buildup | null) => void;
  onSplit: (index: number) => void;
}

/**
 * Opbygning (v8 `bsel`): a select-like button that opens the candidate list
 * (filtered by the type's category — the model's when `measure` set one),
 * then the current build-up's other layers, each with "Opret som egen
 * type". A project's own build-up (`origin: project`) says so.
 */
export function BuildupPicker({ type, types, library, current, mixed, clearable, canEdit, busy, onPick, onSplit }: BuildupPickerProps) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const list = candidates(type, library);
  return (
    <div className={styles.bselWrap}>
      <SelectButton
        variant="filled"
        size="lg"
        block
        placeholder={!current}
        aria-expanded={open}
        aria-controls={listId}
        aria-label={current ? `Opbygning: ${current.name}. Skift opbygning` : 'Vælg opbygning'}
        disabled={!canEdit}
        onClick={() => setOpen((o) => !o)}
      >
        {current ? current.name : mixed ? 'Forskellige opbygninger' : 'Vælg opbygning'}
      </SelectButton>
      <div className={styles.sub}>
        {current ? `${layerText(current)}${current.note ? ` · ${current.note}` : ''}` : 'Giver forslag til EAK og tons'}
      </div>
      {open && (
        <div className={styles.opts} id={listId} role="group" aria-label="Vælg opbygning">
          {list.length === 0 && <span className={styles.sub}>Ingen opbygninger for denne kategori</span>}
          {list.map((b) => (
            <button
              key={b.id}
              type="button"
              className={styles.opt}
              aria-pressed={current?.id === b.id}
              disabled={busy}
              onClick={() => {
                setOpen(false);
                if (current?.id !== b.id) onPick(b);
              }}
            >
              <span className={styles.optName}>
                {b.name}
                {b.origin === 'project' && <span className={styles.optOrigin}>projektets egen</span>}
              </span>
              <span className={styles.optLayers}>{layerText(b)}</span>
            </button>
          ))}
          {clearable && current && (
            <button
              type="button"
              className={styles.opt}
              disabled={busy}
              onClick={() => {
                setOpen(false);
                onPick(null);
              }}
            >
              <span className={styles.optName}>Ingen opbygning</span>
              <span className={styles.optLayers}>Tonsene beregnes ikke — tast vægten selv</span>
            </button>
          )}
        </div>
      )}
      {current?.others.map((layer, i) => (
        <div key={`${layer.name ?? layer.material}:${i}`} className={styles.layer}>
          <span>{otherLayerLine(layer, type)}</span>
          {layerExists(types, layer) ? (
            <span>oprettet som egen type</span>
          ) : (
            canEdit && (
              <TextLink size="sm" nowrap disabled={busy} onClick={() => onSplit(i)}>
                Opret som egen type
              </TextLink>
            )
          )}
        </div>
      ))}
    </div>
  );
}
